
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getCategoriesApi } from '../../api/communities';
import { fetchProfile } from '../../store/slices/profileSlice';
import {
  fetchCommunities,
  setSearchFilter,
  setSelectedCategory,
  clearFilters
} from '../../store/slices/communitySlice';
import './Communities.scss';
import { Button, Row, Col, Container, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { CommunityCard } from './components/CommunityCard';


const MOCK_COMMUNITIES = [];

// Generate exactly 248 items to match mockup count
const GENERATED_COMMUNITIES = [];
if (MOCK_COMMUNITIES.length > 0) {
  for (let i = 0; i < 248; i++) {
    const original = MOCK_COMMUNITIES[i % MOCK_COMMUNITIES.length];
    if (!original) continue;
    GENERATED_COMMUNITIES.push({
      ...original,
      _id: `mock-${i + 1}`,
      title: i < MOCK_COMMUNITIES.length
        ? original.title
        : `${original.title} #${Math.floor(i / MOCK_COMMUNITIES.length) + 1}`,
      membersCount: Math.max(12, Math.round(original.membersCount * (1 - (i / 350)))),
      createdAt: new Date(Date.now() - i * 3600000).toISOString()
    });
  }
}

export const Communities = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    communities,
    loading,
    error,
    filters
  } = useSelector((state) => state.community);

  const [categories, setCategories] = useState([
    { _id: '', name: 'All Categories', icon: '🌐' }
  ]);

  const COLOR_CLASSES = [
    'primary',
    'success',
    'warning',
    'danger',
    'info',
    'secondary',
    'dark'
  ];

  const getRandomColor = () => {
    return COLOR_CLASSES[Math.floor(Math.random() * COLOR_CLASSES.length)];
  };

  const [showMyCommunities, setShowMyCommunities] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { profile, loading: profileLoading, fetched: profileFetched, error: profileError } = useSelector((state) => state.profile);

  useEffect(() => {
    if (isAuthenticated && !profile && !profileLoading?.profile && !profileFetched && !profileError?.profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, profileLoading, profileFetched, profileError, dispatch]);

  const handleCreateCommunityClick = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/create-community' } });
      return;
    }
    if (profile) {
      if (profile.isSubscriptionEnabled === true && profile.has_active_subscription !== true) {
        navigate('/profile?tab=subscriptions');
        return;
      }
    }
    navigate('/create-community');
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategoriesApi();
        if (data && data.data) {
          const emojiMap = {
            'Language': '🗣️',
            'Relationships': '❤️',
            'Parenting': '👶',
            'Mindset': '🧠',
            'Career': '💼',
            'Science': '🔬',
            'Technology': '💻',
            'Health': '🌱',
            'Education': '📚',
            'Business': '🏢',
            'Art & Design': '🎨',
            'Music': '🎵',
            'Travel': '✈️',
            'Motivation': '🔥',
            'Books': '📖',
            'Finance': '💰',
            'Writing': '✍️',
            'Entertainment': '🎬',
            'Gaming': '🎮',
            'Hobbies': '🎨'
          };
          const apiCategories = data.data.map(cat => {
            const match = cat.name.match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
            const cleanName = match ? match[1].trim() : cat.name;
            const cleanIcon = (match && match[2]) ? match[2] : (emojiMap[cleanName] || '🌐');
            return {
              _id: cat._id,
              name: cleanName,
              icon: cleanIcon,
            };
          });
          setCategories([
            { _id: '', name: 'All Categories', icon: '🌐' },
            ...apiCategories
          ]);
        }
      } catch (err) {
        setCategories([{ _id: '', name: 'All Categories', icon: '🌐' }]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: filters.search,
        category: filters.selectedCategory,
      };
      dispatch(fetchCommunities(params));
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.search, filters.selectedCategory, dispatch]);

  const handleSearch = (e) => {
    dispatch(setSearchFilter(e.target.value));
  };

  const handleCategoryClick = (categoryId) => {
    dispatch(setSelectedCategory(categoryId));
  };

  const handleCommunityClick = (communityId) => {
    navigate('/community-detail', { state: { id: communityId } });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('popular'); // 'popular' | 'newest'

  // Use real API data if available, else fall back to generated mock data
  const communityList = communities.length > 0 ? communities : (!loading ? GENERATED_COMMUNITIES : []);

  const displayedCommunities = React.useMemo(() => {
    let list = communityList;
    if (showMyCommunities && user) {
      list = communityList.filter(c => {
        const ownerId = typeof c.owner === 'object' ? c.owner?._id : c.owner;
        return ownerId === user._id;
      });
    }

    // Sorting
    const sorted = [...list];
    if (sortBy === 'popular') {
      sorted.sort((a, b) => (b.membersCount || 0) - (a.membersCount || 0));
    } else if (sortBy === 'newest') {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [communityList, showMyCommunities, user, sortBy]);

  const itemsPerPage = 20; // 20 per page matching mockup
  const totalItems = displayedCommunities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.selectedCategory, showMyCommunities, sortBy]);

  const paginatedCommunities = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return displayedCommunities.slice(start, start + itemsPerPage);
  }, [displayedCommunities, currentPage, itemsPerPage]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('ellipsis1');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('ellipsis2');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="communities-page">
      {/* Hero Section */}
      <section className="hero-banner py-5">
        <Container fluid="xl">
          <Row className="g-4">
            <Col lg={5} md={6}>
              <div className="content-wrapper">
                <div className="hero-heading mb-3">
                  Explore <span className="highlight-purple">Communities</span>
                </div>
                <div className="hero-subtitle mb-3">
                  Join learning communities, connect with amazing people, and grow together.
                </div>
                <Button variant='purple' className="btn-create-community rounded-pill btn-custom" onClick={handleCreateCommunityClick}>
                  <i className="bi bi-plus-lg"></i> Create Community
                </Button>
              </div>
            </Col>

            <Col lg={7} md={6}>
              <Row className="hero-features-row g-2 g-lg-3">
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-people-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Connect</div>
                      <div className="feature-desc text-muted">Meet amazing people and share ideas.</div>
                    </div>
                  </div>
                </Col>
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-book-half fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Learn</div>
                      <div className="feature-desc text-muted">Access valuable resources and improve skills.</div>
                    </div>
                  </div>
                </Col>
                <Col xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-bar-chart-line-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Grow</div>
                      <div className="feature-desc text-muted">Build your knowledge and achieve goals.</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Categories Section */}
      <section className="categories-section bg-white pt-4 pb-0">
        <Container fluid="xl">

          <div className="categories-filter-bar">
            <div className="categories-scroll-wrapper">
              <div className="categories-pills">
                {categories.map((cat, idx) => (
                  <button
                    key={cat._id || idx}
                    onClick={() => handleCategoryClick(cat._id)}
                    className={`category-pill-btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-medium ${filters.selectedCategory === cat._id
                      ? 'active'
                      : ''
                      }`}
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{cat.icon || '🌐'}</span>
                    <span>{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Dropdown - always inline on the right */}
            <div className="sort-dropdown-wrapper">
              <Dropdown onSelect={(key) => setSortBy(key)}>
                <Dropdown.Toggle id="dropdown-sort" className="btn-sort px-3 py-2 fw-medium d-flex align-items-center gap-2">
                  <i className="bi bi-sort-down-alt"></i>
                  <span>Sort: {sortBy === 'popular' ? 'Popular' : 'Newest'}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow border-0 mt-1" align="end">
                  <Dropdown.Item eventKey="popular" className="py-2 px-3">Popular</Dropdown.Item>
                  <Dropdown.Item eventKey="newest" className="py-2 px-3">Newest</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Container>
      </section>

      {/* Grid Section */}
      <section className="communities-grid-section bg-white section-padding pt-4">
        <Container fluid="xl">
          <div className="wrapper d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            {/* <div className="section-heading m-0">{showMyCommunities ? 'My' : 'Popular'} <span>communities</span></div> */}
            <div className="left-wrapper">
              {!showMyCommunities && totalItems > 0 && (
                <div className="text-muted text-sm fw-semibold mb-2">
                  Showing {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} communities
                </div>
              )}
              {user && (
                <div className="my-communities-switch-wrapper">
                  <Form.Check
                    type="switch"
                    id="my-communities-switch"
                    label="My Communities"
                    className="text-sm fw-medium text-primary m-0"
                    checked={showMyCommunities}
                    onChange={(e) => setShowMyCommunities(e.target.checked)}
                  />
                </div>
              )}
            </div>

            {/* Mobile search bar only */}
            <div className="search-box-wrapper w-100" style={{ maxWidth: "400px" }}>
              <InputGroup className="bg-white border rounded-pill shadow-sm overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700"><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control
                  placeholder="Search communities..."
                  className="border-0 shadow-none px-2"
                  value={filters.search}
                  onChange={handleSearch}
                />
                {filters.search && (
                  <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                    <i className="bi bi-x-circle clear-icon" onClick={() => dispatch(setSearchFilter(''))}></i>
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {paginatedCommunities.length === 0 ? (
                <div className="text-center py-5 text-gray-600">
                  {showMyCommunities ? "You haven't created any communities yet." : "No communities found."}
                </div>
              ) : (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3 g-md-4">
                  {paginatedCommunities.map((community) => (
                    <div className="item-card" key={community._id}>
                      <CommunityCard community={community} />
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="pagination-wrapper d-flex justify-content-center align-items-center gap-2 mt-5">
                  <button
                    className="btn btn-pagination btn-prev border d-flex align-items-center justify-content-center rounded-circle"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    style={{ width: '40px', height: '40px' }}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {getPageNumbers().map((page, idx) => {
                    if (page === 'ellipsis1' || page === 'ellipsis2') {
                      return <span key={`ellipse-${idx}`} className="px-2 text-muted fw-bold">...</span>;
                    }
                    return (
                      <button
                        key={page}
                        className={`btn btn-pagination border d-flex align-items-center justify-content-center rounded-circle fw-medium ${currentPage === page ? 'active bg-primary text-white border-primary' : 'bg-white text-gray-700'}`}
                        onClick={() => setCurrentPage(page)}
                        style={{ width: '40px', height: '40px' }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    className="btn btn-pagination btn-next border d-flex align-items-center justify-content-center rounded-circle"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    style={{ width: '40px', height: '40px' }}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              )}

              {!showMyCommunities && displayedCommunities.length > 0 && (
                <div className="browse-all-link text-gray-700 text-center mt-4 pt-2">
                  We have more communities. <Link to="#" className="text-primary text-decoration-none text-nowrap">Browse All <i className="bi bi-arrow-right"></i></Link>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
};
